import { useReducer, useCallback, useState, useMemo, useEffect } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { getTenantSlugFromHost } from '@/shared/lib/tenant-host'
import { applyTenantTheme, brandingToTheme } from '@/shared/lib/theme'
import { Search, ShieldCheck } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Logo } from '@/shared/components/ui/logo'
import { FormStepper } from '@/admin/components/form-stepper'
import { cn } from '@/shared/lib/utils'
import {
  useForm,
  normaliseClaimField,
  formatReviewValueNode as sharedFormatReviewValue,
} from '@/shared/lib/forms'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { buildFormFieldFromMeta } from '@/shared/lib/forms/generated-metadata'
import { useFormattingContext } from '@/shared/lib/formatting'
import { DisclaimerAck } from '@/shared/components/claims/disclaimer-ack'
import {
  disclaimerAckValue,
  disclaimerBlocks,
  type DisclaimerConfig,
} from '@/shared/components/claims/disclaimer'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useConsumerBranding,
  type ConsumerBranding,
} from '@/consumer/hooks/use-consumer-branding'
import {
  usePublicClaimConfig,
  usePublicClaimLookup,
  usePublicClaimFileUpload,
  usePublicClaimSubmit,
  type HostType,
  type LookupCandidate,
} from '@/consumer/hooks/use-public-claim'

// ── Host-type copy ─────────────────────────────────────

const HOST_COPY: Record<string, {
  stepLabel: string
  heading: string
  subtitle: string
  placeholder: string
  reviewSection: string
  identityLabel: string
}> = {
  VEHICLE: {
    stepLabel: 'Find Vehicle',
    heading: 'Find your vehicle',
    subtitle: 'Enter your plate or policy number to get started.',
    placeholder: 'Search plate or policy number',
    reviewSection: 'Vehicle',
    identityLabel: 'Registration',
  },
  PROPERTY: {
    stepLabel: 'Find Property',
    heading: 'Find your property',
    subtitle: 'Enter your postcode to get started.',
    placeholder: 'Search by postcode',
    reviewSection: 'Property',
    identityLabel: 'Address',
  },
  VESSEL: {
    stepLabel: 'Find Vessel',
    heading: 'Find your vessel',
    subtitle: 'Enter your hull identification number to get started.',
    placeholder: 'Search hull identification number',
    reviewSection: 'Vessel',
    identityLabel: 'HIN',
  },
}

const DEFAULT_COPY = {
  stepLabel: 'Find Registration',
  heading: 'Find your registration',
  subtitle: 'Enter your registration number to get started.',
  placeholder: 'Search registration number',
  reviewSection: 'Registration',
  identityLabel: 'Registration',
}

function getCopy(hostType: HostType) {
  return HOST_COPY[hostType] || DEFAULT_COPY
}

// ── State ───────────────────────────────────────────────

interface LookupData {
  holding_uuid: string
  product_name: string
  display_identity: string
  host_type: HostType
  fields: {
    code_name: string
    display_name: string
    field_type: string
    required: boolean
    configuration_data: Record<string, unknown>
  }[]
}

interface WizardState {
  step: number
  lookupData: LookupData | null
}

type Action =
  | { type: 'LOOKUP_SUCCESS'; data: LookupData }
  | { type: 'NEXT' }
  | { type: 'BACK' }

function reducer(state: WizardState, action: Action): WizardState {
  switch (action.type) {
    case 'LOOKUP_SUCCESS':
      return { ...state, step: 1, lookupData: action.data }
    case 'NEXT':
      return { ...state, step: Math.min(state.step + 1, 2) }
    case 'BACK':
      return { ...state, step: Math.max(state.step - 1, 0) }
    default:
      return state
  }
}

// ── Page ────────────────────────────────────────────────

export default function PublicClaimPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  // The tenant subdomain (`<slug>.domain/claim`) is authoritative; the
  // `:orgSlug` path param is only a generic-host fallback for legacy links.
  const slug = getTenantSlugFromHost() || orgSlug || ''
  const { t } = useTranslation()

  const { data: branding, isLoading: brandingLoading } = useConsumerBranding(slug)
  const { data: config, isLoading: configLoading } = usePublicClaimConfig(slug)

  // First-view page: apply the tenant's authored theme (if any) using the
  // slug-scoped branding, so path-based `/claim/<slug>` links the global
  // provider can't resolve are still on-brand. Un-themed tenants inherit the
  // platform theme.
  const brandTheme = branding?.theme
  useEffect(() => {
    if (brandTheme) applyTenantTheme(brandingToTheme({ theme: brandTheme }))
  }, [brandTheme])
  const [state, dispatch] = useReducer(reducer, { step: 0, lookupData: null })
  const [reviewValues, setReviewValues] = useState<Record<string, unknown>>({})
  // Rich upload envelopes for display-only rendering of file/photo fields in
  // the review step. `reviewValues` (the submission payload) keeps the bare
  // UUID the backend requires for captured_values.
  const [reviewUploads, setReviewUploads] = useState<Record<string, unknown>>(
    {},
  )
  // The claim disclaimer + acknowledgement is shown ONCE, at the end of the
  // flow (Review & Submit) via the ``claim_submit`` context. The earlier
  // ``claim_evidence_upload`` disclaimer was removed so the notice +
  // acknowledgement appear a single time.
  const submitDisclaimer = config?.disclaimers?.claim_submit ?? null
  const [submitAck, setSubmitAck] = useState(false)
  // Require a fresh acknowledgement if the consumer goes back and looks
  // up a different registration.
  useEffect(() => {
    setSubmitAck(false)
  }, [state.lookupData])

  const hostType = config?.host_type || ''
  const copy = getCopy(hostType)
  const steps = [copy.stepLabel, 'Claim Details', 'Review & Submit']

  const lookupMutation = usePublicClaimLookup(slug)
  const uploadMutation = usePublicClaimFileUpload(slug)
  const submitMutation = usePublicClaimSubmit(slug)

  const normalisedFields: FormFieldMeta[] = useMemo(
    () => (state.lookupData?.fields ?? []).map(normaliseClaimField),
    [state.lookupData?.fields],
  )
  const generatedFields = useMemo(
    () => normalisedFields.map((field) => buildFormFieldFromMeta(field, undefined)),
    [normalisedFields],
  )

  const uploadFile = useCallback(
    ({ file, use_case }: { file: File; use_case: string }) =>
      uploadMutation.mutateAsync({ file, use_case }),
    [uploadMutation],
  )

  const handleClaimSubmit = useCallback(
    async (
      values: Record<string, unknown>,
      _ctx: unknown,
      uploads?: Record<string, unknown>,
    ) => {
      setReviewValues(values)
      setReviewUploads(uploads ?? {})
      dispatch({ type: 'NEXT' })
    },
    [],
  )

  const formConfig = useMemo(
    () => ({
      name: 'public-claim-form',
      submit: 'footer' as const,
      fields: generatedFields,
      uploadFile,
    }),
    [generatedFields, uploadFile],
  )

  const { Form: ClaimDetailsForm } = useForm(
    formConfig,
    handleClaimSubmit,
    [slug, normalisedFields],
  )

  const [successData, setSuccessData] = useState<{
    reference: string
  } | null>(null)

  const handleSubmit = useCallback(async () => {
    if (!state.lookupData) return
    // Record the acknowledgement as data on the claim when ticked.
    const captured = { ...reviewValues }
    if (submitDisclaimer && submitAck) {
      captured.disclaimer_acknowledged = disclaimerAckValue(submitDisclaimer)
    }
    try {
      const result = await submitMutation.mutateAsync({
        holding_uuid: state.lookupData.holding_uuid,
        captured_values: captured,
      })
      setSuccessData({ reference: result.reference })
    } catch (err: unknown) {
      toastApiError(err, 'Failed to submit claim')
    }
  }, [state.lookupData, reviewValues, submitMutation, submitDisclaimer, submitAck])

  // Online claims switched off for this tenant: say so instead of the form.
  // Checked before the config wait, since that endpoint 404s when off.
  if (branding?.feature_flags?.consumer_claims === false) {
    return (
      <PublicClaimShell branding={branding}>
        <div
          className="mx-auto max-w-[520px] py-[60px] text-center"
          data-testid="public-claim-unavailable"
        >
          <h2 className="mb-3 font-display text-[32px] text-[var(--ink)]">
            {t('publicClaim.unavailableTitle', 'Online claims are not available')}
          </h2>
          <p className="text-[13px] leading-[1.7] text-[var(--ink-3)]">
            {t(
              'publicClaim.unavailableBody',
              'Please contact us or the place you bought your product to raise a claim.',
            )}
          </p>
        </div>
      </PublicClaimShell>
    )
  }

  if (brandingLoading || configLoading) {
    return (
      <PublicClaimShell branding={null} loading>
        <div className="flex items-center justify-center py-20">
          <p className="text-[13px] text-[var(--ink-3)]">Loading...</p>
        </div>
      </PublicClaimShell>
    )
  }

  if (successData) {
    return (
      <PublicClaimShell branding={branding ?? null}>
        <div className="mx-auto max-w-[780px] py-[60px] text-center">
          <div className="mb-4">
            <ShieldCheck className="mx-auto h-8 w-8 text-[var(--green)]" />
          </div>
          <h2 className="mb-3 font-display text-[48px] tracking-[-0.01em] text-[var(--ink)]">
            {successData.reference}
          </h2>
          <div className="mx-auto mb-5 h-[0.5px] w-12 bg-[var(--rule-strong)]" />
          <p className="mb-8 text-[13px] leading-[1.7] tracking-[0.04em] text-[var(--ink-3)]">
            Claim submitted successfully. A confirmation<br />
            email has been sent to you.
          </p>
          {state.lookupData && (
            <p className="mb-8 text-[12px] text-[var(--ink-3)]">
              {state.lookupData.product_name} &middot; {state.lookupData.display_identity}
            </p>
          )}
        </div>
      </PublicClaimShell>
    )
  }

  return (
    <PublicClaimShell branding={branding ?? null}>
      <div className="mx-auto max-w-[600px] py-8">
        <div className="mb-8">
          <FormStepper steps={steps} currentStep={state.step} />
        </div>

        {state.step === 0 && (
          <StepSearch
            copy={copy}
            lookupMutation={lookupMutation}
            onFound={(data) => dispatch({ type: 'LOOKUP_SUCCESS', data })}
          />
        )}
        {state.step === 1 && (
          <StepClaimDetails
            lookupData={state.lookupData!}
            Form={ClaimDetailsForm}
            generatedFields={generatedFields}
            uploading={uploadMutation.isPending}
            onBack={() => dispatch({ type: 'BACK' })}
          />
        )}
        {state.step === 2 && (
          <StepReview
            lookupData={state.lookupData!}
            copy={copy}
            fields={normalisedFields}
            values={reviewValues}
            uploads={reviewUploads}
            back={() => dispatch({ type: 'BACK' })}
            onSubmit={handleSubmit}
            isSubmitting={submitMutation.isPending}
            disclaimer={submitDisclaimer}
            acknowledged={submitAck}
            onAcknowledgedChange={setSubmitAck}
          />
        )}
      </div>
    </PublicClaimShell>
  )
}

// ── Shell ───────────────────────────────────────────────

function PublicClaimShell({
  branding,
  loading = false,
  children,
}: {
  branding: ConsumerBranding | null
  loading?: boolean
  children: React.ReactNode
}) {
  const showOrgLogo =
    branding &&
    (branding.branding_mode === 'BRANDED' || branding.branding_mode === 'COBRANDED') &&
    branding.org_logo_url

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg)]">
      <div className="bg-[var(--green)] px-5 pb-8 pt-10">
        <div className="mx-auto max-w-[600px]">
          <div className="mb-5 flex items-center gap-3">
            {showOrgLogo ? (
              <img
                src={branding.org_logo_url!}
                alt={branding.org_name || ''}
                className="h-8 max-w-[180px] object-contain brightness-0 invert"
              />
            ) : loading ? (
              <div className="h-8" aria-hidden />
            ) : (
              <Logo size="sm" variant="white" />
            )}
            {branding?.branding_mode === 'COBRANDED' && (
              <span className="text-[10px] tracking-[0.08em] text-white/50">
                Powered by iWarranty
              </span>
            )}
          </div>
          <h1 className="mb-1 text-[22px] text-white">Warranty Claim Form</h1>
          <p className="text-[13px] text-white/60">
            Please fill out this form to submit a warranty claim
          </p>
        </div>
      </div>
      <main className="flex-1 px-5">
        {children}
      </main>
    </div>
  )
}

// ── Step 0: Search ──────────────────────────────────────

type CopyConfig = ReturnType<typeof getCopy>

function StepSearch({
  copy,
  lookupMutation,
  onFound,
}: {
  copy: CopyConfig
  lookupMutation: ReturnType<typeof usePublicClaimLookup>
  onFound: (data: LookupData) => void
}) {
  const [query, setQuery] = useState('')
  const [candidates, setCandidates] = useState<LookupCandidate[] | null>(null)

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim()
    if (!trimmed) return
    setCandidates(null)
    try {
      const data = await lookupMutation.mutateAsync({ query: trimmed })
      if (data.candidates && data.candidates.length > 0) {
        setCandidates(data.candidates)
      } else if (data.holding_uuid && data.fields) {
        onFound(data as LookupData)
      }
    } catch {
      // Error state handled via lookupMutation.isError
    }
  }, [query, lookupMutation, onFound])

  const handleSelectCandidate = useCallback(async (candidate: LookupCandidate) => {
    try {
      const data = await lookupMutation.mutateAsync({
        query: query.trim(),
        holding_uuid: candidate.holding_uuid,
      })
      if (data.holding_uuid && data.fields) {
        onFound(data as LookupData)
      }
    } catch {
      // Error state handled via lookupMutation.isError
    }
  }, [query, lookupMutation, onFound])

  const errorMessage =
    lookupMutation.isError
      ? ((lookupMutation.error as { response?: { data?: { message?: string } } })
          ?.response?.data?.message ?? 'Not found, please try again.')
      : null

  return (
    <div>
      <h1 className="mb-2 text-[22px] text-[var(--ink)]">{copy.heading}</h1>
      <p className="mb-6 text-[13px] text-[var(--ink-3)]">{copy.subtitle}</p>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSearch()
        }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--ink-3)]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={copy.placeholder}
            className="pl-9"
            autoFocus
          />
        </div>
        <Button
          type="submit"
          disabled={!query.trim() || lookupMutation.isPending}
          className={cn(
            greenBtnClass,
            (!query.trim() || lookupMutation.isPending) && 'opacity-70',
          )}
        >
          {lookupMutation.isPending ? 'Searching...' : 'Search'}
        </Button>
      </form>

      {errorMessage && (
        <p className="mt-3 text-[13px] text-red-500">{errorMessage}</p>
      )}

      {/* Multi-result picker for property tenants */}
      {candidates && candidates.length > 0 && (
        <div className="mt-4">
          <p className="mb-3 text-[12px] text-[var(--ink-3)]">
            Multiple results found. Select your property:
          </p>
          <div className="flex flex-col gap-2">
            {candidates.map((c) => (
              <button
                key={c.holding_uuid}
                type="button"
                onClick={() => handleSelectCandidate(c)}
                disabled={lookupMutation.isPending}
                className="cursor-pointer rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-4 py-3 text-left transition-colors hover:border-[var(--ink-3)]"
              >
                <p className="text-[14px] text-[var(--ink)]">{c.product_name}</p>
                <p className="text-[12px] text-[var(--ink-3)]">{c.display_identity}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Step 1: Claim Details ───────────────────────────────

function StepClaimDetails({
  lookupData,
  Form,
  generatedFields,
  uploading,
  onBack,
}: {
  lookupData: LookupData
  Form: ReturnType<typeof useForm>['Form']
  generatedFields: ReturnType<typeof buildFormFieldFromMeta>[]
  uploading: boolean
  onBack: () => void
}) {
  return (
    <div>
      <div className="mb-6 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-4 py-3">
        <p className="text-[14px] text-[var(--ink)]">{lookupData.product_name}</p>
        <p className="text-[12px] text-[var(--ink-3)]">{lookupData.display_identity}</p>
      </div>

      <h2 className="mb-4 text-[18px] text-[var(--ink)]">Claim Details</h2>

      {generatedFields.length === 0 ? (
        <div>
          <p className="py-5 text-[13px] text-[var(--ink-3)]">
            No additional details required for this claim type.
          </p>
          <div className="mt-6 flex justify-between">
            <Button onClick={onBack} variant="outline" className={ghostBtnClass}>
              Back
            </Button>
          </div>
        </div>
      ) : (
        <Form className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {() => (
            <div className="col-span-full mt-6 flex justify-between">
              <Button onClick={onBack} type="button" variant="outline" className={ghostBtnClass}>
                Back
              </Button>
              <Button type="submit" disabled={uploading} variant="default" className={greenBtnClass}>
                {uploading ? 'Uploading...' : 'Next'}
              </Button>
            </div>
          )}
        </Form>
      )}
    </div>
  )
}

// ── Step 2: Review & Submit ─────────────────────────────

function StepReview({
  lookupData,
  copy,
  fields,
  values,
  uploads,
  back,
  onSubmit,
  isSubmitting,
  disclaimer,
  acknowledged,
  onAcknowledgedChange,
}: {
  lookupData: LookupData
  copy: CopyConfig
  fields: FormFieldMeta[]
  values: Record<string, unknown>
  uploads: Record<string, unknown>
  back: () => void
  onSubmit: () => void
  isSubmitting: boolean
  disclaimer: DisclaimerConfig | null
  acknowledged: boolean
  onAcknowledgedChange: (acknowledged: boolean) => void
}) {
  const { locale, currency } = useFormattingContext()
  return (
    <div>
      <p className="mb-5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
        Review & Submit
      </p>

      <div className="mb-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-4 py-[14px]">
        <p className="mb-2.5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
          {copy.reviewSection}
        </p>
        <div className="flex items-baseline justify-between border-b-[0.5px] border-[var(--rule)] py-1.5">
          <span className="text-[12px] text-[var(--ink-3)]">Product</span>
          <span className="text-[13px] text-[var(--ink)]">{lookupData.product_name}</span>
        </div>
        <div className="flex items-baseline justify-between py-1.5">
          <span className="text-[12px] text-[var(--ink-3)]">{copy.identityLabel}</span>
          <span className="text-[13px] text-[var(--ink)]">{lookupData.display_identity}</span>
        </div>
      </div>

      {fields.length > 0 && (
        <div className="mb-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-4 py-[14px]">
          <p className="mb-2.5 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Claim Details
          </p>
          {fields.map((field) => {
            const value = values[field.code_name]
            return (
              <div
                key={field.code_name}
                className="flex items-baseline justify-between border-b-[0.5px] border-[var(--rule)] py-1.5 last:border-b-0"
              >
                <span className="text-[12px] text-[var(--ink-3)]">
                  {field.display_name}
                </span>
                <span className="text-[13px] text-[var(--ink)]">
                  {sharedFormatReviewValue(field, value, uploads, {
                    locale,
                    currency,
                  })}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {disclaimer && (
        <DisclaimerAck
          className="mb-4"
          disclaimer={disclaimer}
          acknowledged={acknowledged}
          onAcknowledgedChange={onAcknowledgedChange}
        />
      )}

      <div className="mt-6 flex justify-between">
        <Button onClick={back} disabled={isSubmitting} variant="outline" className={ghostBtnClass}>
          Back
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || disclaimerBlocks(disclaimer, acknowledged)}
          variant="default"
          className={cn(greenBtnClass, isSubmitting && 'opacity-70')}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Claim'}
        </Button>
      </div>
    </div>
  )
}

// ── Shared button classes ───────────────────────────────

const greenBtnClass =
  'cursor-pointer rounded-[6px] bg-[var(--green)] px-6 py-2.5 text-[12px] font-normal uppercase tracking-[0.08em] text-white'

const ghostBtnClass =
  'cursor-pointer rounded-[6px] border border-input bg-transparent px-6 py-2.5 text-[12px] font-normal uppercase tracking-[0.08em] text-[var(--ink-2)]'
